import React from 'react';
import { Insight } from '@/api/insightsApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { chartColors } from './chartColors';
import { CustomTooltip } from './CustomTooltip';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useRechartsTheme } from '@/hooks/useRechartsTheme';

interface NegativeFloatVisualizationProps {
  insight: Insight;
}

export const NegativeFloatVisualization: React.FC<NegativeFloatVisualizationProps> = ({ insight }) => {
  const { theme } = useAppTheme();
  const chartTheme = useRechartsTheme();
  const activitiesAffected = insight.activitiesAffected || 0;
  const finishDelta = insight.finishDelta;
  const negativeFloatDays = finishDelta ? Math.abs(finishDelta) : 0;

  const floatDistributionData = [
    { category: 'Negative Float', count: activitiesAffected, color: chartColors.danger },
    { category: 'Zero Float', count: Math.floor(activitiesAffected * 0.3), color: chartColors.warning },
    { category: 'Positive Float', count: Math.floor(activitiesAffected * 2), color: chartColors.success },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Activities Affected</div>
          <div className="text-2xl font-bold text-red-400">{activitiesAffected}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Max Negative Float</div>
          <div className="text-2xl font-bold text-red-400">{negativeFloatDays.toFixed(1)} days</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Schedule Compression</div>
          <div className="text-2xl font-bold text-amber-400">Critical</div>
        </div>
      </div>

      {/* Float Distribution */}
      <div className={`rounded-lg p-4 border ${
        theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h4 className={`text-sm font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Float Distribution</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={floatDistributionData}
            margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
            <XAxis 
              dataKey="category" 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
            />
            <YAxis 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
              label={{ value: 'Activities', angle: -90, position: 'insideLeft', style: { fill: chartTheme.textSecondary } }}
            />
            <Tooltip 
              content={<CustomTooltip formatter={(value: number) => [`${value} activities`, 'Count']} />}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {floatDistributionData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};



